import React, { useEffect, useState } from 'react';
import EmployeeForm from './info';
import { useRedux } from 'use-redux';
import { userApi } from '@api/index';
import { addError, addStatus } from '@actions/utils';
import { EmployeeData, User } from '@/types/user';
import { formatDate, UTC_DATE_FORMAT } from '@utils/date';
import { compose } from 'recompose';
import { needsAuth } from '@components/utils/authHoc';
import Select from 'react-select';
import CheckBox from '@ui/CheckBox';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import Price from '../gui/admin/salary/components/price';
import { defaultPriceConfig } from '../gui/admin/salary/constants';
import { PriceConfig } from '@/types/salary';
import TeacherParamsForm from './TeacherParamsForm';

interface TeacherParams {
  childAge: { from: number; to: number };
  canTeachCoursesIds: string[];
  canTeachLanguages: string[];
}

const EmployeeProfile = () => {
  const [{ auth }, dispatch] = useRedux();
  const isAllowUserEdit = ['admin', 'mentor'].includes(auth.user.group);
  const [userId, setUserId] = useState<string | undefined>();
  const [profile, setProfile] = useState({
    phone: auth.user.phone,
    ...auth.user.profile,
  });

  const [teacherParams, setTeacherParams] = useState<TeacherParams | null>({
    childAge: auth.user.childAge,
    canTeachCoursesIds: auth.user.canTeachCoursesIds,
    canTeachLanguages: auth.user.canTeachLanguages,
  });
  const [employees, setEmployees] = useState<User[]>([]);

  const handleUpdateInfo = async (info: EmployeeData) => {
    try {
      const { birthDate, contractDate, ...rest } = info;
      await userApi.updateProfile(
        {
          birthDate: formatDate(birthDate, UTC_DATE_FORMAT),
          contractDate: formatDate(contractDate, UTC_DATE_FORMAT),
          ...rest,
        },
        userId
      );
      dispatch(addStatus('Изменения сохранены'));
    } catch (error) {
      dispatch(addError(error));
    }
  };

  const handleProfilePropChange = async (
    e: CheckboxChangeEvent,
    propName: string
  ) => {
    try {
      const { checked } = e.target;
      await userApi.updateProfile({ [propName]: checked }, userId);
      setProfile(prev => ({ ...prev, [propName]: checked }));
      dispatch(addStatus('Изменения сохранены'));
    } catch (error) {
      dispatch(addError(error));
    }
  };

  const handleTeacherParamsChange = async (value: any, propName: string) => {
    try {
      await userApi.updateTeacherParams({ [propName]: value }, userId);
      setTeacherParams(prev => (prev ? { ...prev, [propName]: value } : prev));
      dispatch(addStatus('Изменения сохранены'));
    } catch (error) {
      dispatch(addError(error));
    }
  };

  const onSavePriceConfig = (config: PriceConfig) => {
    userApi.updateProfile({ priceConfig: config }, userId).then(() => {
      dispatch(addStatus('Изменения сохранены'));
    });
  };

  useEffect(() => {
    if (!isAllowUserEdit) return;

    userApi.getAllEmployeesUsers().then(
      users => setEmployees(users),
      error => dispatch(addError(error))
    );
  }, [dispatch, isAllowUserEdit]);

  useEffect(() => {
    setTeacherParams(null);
    userApi
      .getProfile(userId)
      .then(e => {
        setProfile({ ...e.profile, phone: e.phone });
        setTeacherParams({
          childAge: e.childAge,
          canTeachCoursesIds: e.canTeachCoursesIds,
          canTeachLanguages: e.canTeachLanguages,
        });
      })
      .catch(error => dispatch(addError(error)));
  }, [dispatch, userId]);

  const searchItem = (item: any, searchText: string) => {
    if (!item.data) return false;

    const { email = '', realname = '', profile = {} } = item.data;
    const text = searchText.toLocaleLowerCase().trim();

    return [email, realname, String(profile.taxNumber || '')]
      .map(value => value.toLowerCase().includes(text))
      .some(Boolean);
  };

  return (
    <div className="container">
      <h1>Профиль Пользователя</h1>
      {isAllowUserEdit && (
        <>
          <Select
            isClearable
            isSearchable
            isMulti={false}
            hideSelectedOptions={false}
            options={employees}
            filterOption={searchItem}
            getOptionLabel={option => `${option.realname} (${option.email})`}
            getOptionValue={option => option._id}
            onChange={option => setUserId(option && option._id)}
          />
          <br />
          {userId && (
            <div>
              <CheckBox
                onChange={e => handleProfilePropChange(e, 'requireMeetRecords')}
                checked={profile.requireMeetRecords !== false}
              >
                Обязан вести уроки в meet
              </CheckBox>

              <CheckBox
                onChange={e => handleProfilePropChange(e, 'isTeacherBlocked')}
                checked={profile.isTeacherBlocked}
              >
                Не допущен к пробным урокам
              </CheckBox>

              <CheckBox
                onChange={e =>
                  handleProfilePropChange(e, 'hasRecordsPermission')
                }
                checked={profile.hasRecordsPermission}
              >
                Доступ к записям
              </CheckBox>

              <br />

              {teacherParams && (
                <TeacherParamsForm
                  onCanTeachCoursesChange={value =>
                    handleTeacherParamsChange(value, 'canTeachCoursesIds')
                  }
                  onCanTeachLanguagesChange={value =>
                    handleTeacherParamsChange(value, 'canTeachLanguages')
                  }
                  onChildAgeChange={value =>
                    handleTeacherParamsChange(value, 'childAge')
                  }
                  teacherParams={teacherParams}
                />
              )}
              <br />
              <p>
                <b>Ставка</b>
              </p>
              <Price
                onSubmit={onSavePriceConfig}
                config={profile.priceConfig || defaultPriceConfig}
              />
            </div>
          )}
        </>
      )}
      <br />
      <EmployeeForm
        userId={userId || auth.user._id}
        isMyProfile={!userId || userId === auth.user._id}
        employee={profile}
        onSubmit={handleUpdateInfo}
      />
    </div>
  );
};

export default compose(needsAuth)(EmployeeProfile);
